import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { Button, Input } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useApp } from '../../context/AppContext';
import { requestLocationPermission, getCurrentPosition } from '../../services/location';
import { colors } from '../../theme';

/**
 * City picker for the delivery/service location.
 *
 * Search goes through our own /maps/cities endpoint, which asks Google Places
 * for Indian cities - so smaller places like Yamuna Nagar or Jagadhri resolve
 * the same as the metros. The shortlist below is only a starting point before
 * anyone types; it is never what search filters against.
 */
const POPULAR = [
  { city: 'Delhi', label: 'Delhi', latitude: 28.7041, longitude: 77.1325 },
  { city: 'Gurugram', label: 'Gurugram, Haryana', latitude: 28.4595, longitude: 77.0266 },
  { city: 'Noida', label: 'Noida, Uttar Pradesh', latitude: 28.5355, longitude: 77.3910 },
  { city: 'Yamuna Nagar', label: 'Yamuna Nagar, Haryana', latitude: 30.1290, longitude: 77.2674 },
  { city: 'Karnal', label: 'Karnal, Haryana', latitude: 29.6857, longitude: 76.9905 },
  { city: 'Panipat', label: 'Panipat, Haryana', latitude: 29.3909, longitude: 76.9635 },
  { city: 'Rohtak', label: 'Rohtak, Haryana', latitude: 28.8955, longitude: 76.6066 },
  { city: 'Hisar', label: 'Hisar, Haryana', latitude: 29.1492, longitude: 75.7217 },
];

const LocationPage = () => {
  const navigate = useNavigate();
  const { location, setLocation } = useApp();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [locating, setLocating] = useState(false);
  const [failed, setFailed] = useState(false);
  const seq = useRef(0);

  // Debounced so a fast typist costs one Places call, not one per keystroke.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); setFailed(false); return undefined; }
    setSearching(true);
    const mine = ++seq.current;
    const timer = setTimeout(async () => {
      try {
        const res = await api.maps.cities(q);
        if (mine !== seq.current) return;          // a newer search won
        setResults(res.data || []);
        setFailed((res.data || []).length === 0);
      } catch (e) {
        if (mine !== seq.current) return;
        setResults([]);
        setFailed(true);
      } finally {
        if (mine === seq.current) setSearching(false);
      }
    }, 320);
    return () => clearTimeout(timer);
  }, [query]);

  const pick = async (next) => {
    await setLocation(next);
    navigate(-1);
  };

  /** A search result carries no coordinates - resolve the place first. */
  const chooseResult = async (item) => {
    setResolving(item.placeId);
    try {
      const res = await api.maps.cityDetails(item.placeId);
      const d = res.data || {};
      if (!d.coordinates) throw new Error('no coordinates');
      const [longitude, latitude] = d.coordinates;
      await pick({
        source: 'manual',
        city: d.city || item.city,
        district: d.district || '',
        state: d.state || '',
        label: [d.city || item.city, d.state].filter(Boolean).join(', '),
        latitude,
        longitude,
      });
    } catch (e) {
      setFailed(true);
    } finally { setResolving(null); }
  };

  const useCurrent = async () => {
    setLocating(true);
    try {
      const ok = await requestLocationPermission();
      const fix = ok ? await getCurrentPosition() : null;
      if (!fix) return;

      const [longitude, latitude] = fix.coordinates;
      let city = location.city;
      let district = '';
      let state = '';
      let label = 'Current location';
      let address = '';
      let pincode = '';
      try {
        const rev = await api.maps.reverse({ lat: latitude, lng: longitude });
        const a = rev.data || {};
        city = a.city || city;
        district = a.district || '';
        state = a.state || '';
        label = [a.city, a.state].filter(Boolean).join(', ') || a.address || label;
        // The street address as the map names it - what "where am I" should say.
        address = a.address || '';
        pincode = a.pincode || '';
      } catch (e) { /* keep the coordinates even if naming fails */ }

      await pick({
        city, district, state, label, latitude, longitude, source: 'gps', address, pincode,
      });
    } finally { setLocating(false); }
  };

  const typing = query.trim().length >= 2;
  const rows = typing ? results : POPULAR;

  return (
    <div style={{ maxWidth: 560 }}>
      <PageHeader title="Choose your location" subtitle="We show partners near this point" />

      <div className="card sh-sm">
        <Input
          icon="🔍"
          placeholder="Search any city, town or district"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <Button
          title={locating ? 'Getting your location…' : 'Use my current location'}
          icon="🎯"
          variant="ghost"
          full
          loading={locating}
          onClick={useCurrent}
        />
        {location.source === 'gps' && location.address ? (
          <div className="t-tiny" style={{ marginTop: 6 }}>You are at: {location.address}</div>
        ) : null}

        <div
          className="t-tiny"
          style={{ letterSpacing: 1, margin: '20px 0 8px', display: 'flex', justifyContent: 'space-between' }}
        >
          <span>{typing ? 'SEARCH RESULTS' : 'POPULAR CITIES'}</span>
          {searching ? <span>Searching…</span> : null}
        </div>

        {rows.map((c, i) => {
          const active = !c.placeId && location.city === c.city;
          const busy = resolving === c.placeId;
          return (
            <Reveal key={c.placeId || c.city} delay={stagger(i, 30)}>
              <button
                type="button"
                disabled={Boolean(resolving)}
                onClick={() => (c.placeId ? chooseResult(c) : pick({ ...c, source: 'manual' }))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                  background: active ? colors.primaryLight : 'var(--card, #fff)',
                  border: `1px solid ${active ? colors.primary : colors.line}`,
                  borderRadius: 12, padding: 12, marginBottom: 8,
                  opacity: resolving && !busy ? 0.6 : 1,
                }}
              >
                <span style={{ fontSize: 17 }}>🏙️</span>
                <span className="grow">
                  <span className="t-body" style={{ fontWeight: 600, display: 'block' }}>
                    {c.city || c.label}
                  </span>
                  {c.region ? <span className="t-tiny">{c.region}</span> : null}
                </span>
                {busy ? <span className="t-tiny">…</span> : null}
                {active ? <span style={{ color: colors.primary, fontWeight: 800 }}>✓</span> : null}
              </button>
            </Reveal>
          );
        })}

        {!rows.length && !searching ? (
          <div className="t-small" style={{ textAlign: 'center', padding: 20 }}>
            {failed
              ? 'No city matched. Try the district name, or a bigger town nearby.'
              : 'Search any city, town or district in India.'}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LocationPage;
